// lib
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RouteComponentProps, useParams } from 'react-router'

// components
import MetaTags from 'src/components/meta_tags/meta_tags'
import HeaderEventCategory from 'src/containers/header/subheader/header_event_category'
import EventCategoryFeed from 'src/containers/smart_components/feeds/event_feed'
import PageTitleEventCategory from 'src/containers/smart_components/page_title/page_title_event_category'

// constants & interfaces
import { FEED_DEFAULT_PER_PAGE, FEED_TYPE_EVENT } from 'src/constants/feed'
import {
  EVENTSFEED_CATEGORY_TITLE,
  EVENTSFEED_DESCRIPTION,
  EVENTSFEED_OG_IMAGE
} from 'src/constants/meta_tags'

// helpers
import { EventsListQueryVariables, useEventsList } from '@lopo-tech/lopo-api'
import { useSetActiveFilter } from 'src/actions/filters/filters_hooks'
import { PREVIEW_IMAGE_HEIGHT, PREVIEW_IMAGE_WIDTH } from 'src/constants/image'
import { useIsPostCreateOpen, usePageTracking } from 'src/pages/hooks/hooks'
import { useGetAppLocationShape, useGetBoundingBox } from 'src/reducers/app-state/app-state_hooks'
import { useSelectActiveFilter, useSelectDateFilter } from 'src/reducers/filters/filters_hooks'
import { useSelectUser } from 'src/reducers/user/user_hooks'
import { LayoutFeaturedContainer } from '../layout/layout_featured_container'
import DateFormatter from '../utils/date_formatter/date_formatter'
import { createMarkersList } from '../utils/feeds'

const EventCategoryFeedPage: React.FC<RouteComponentProps> = ({ match }) => {
  const locationShape = useGetAppLocationShape()
  const boundingBox = useGetBoundingBox()
  const user = useSelectUser()
  const isPostCreateOpen = useIsPostCreateOpen(match)

  const dateFilterRange = useSelectDateFilter()
  const activeFilter = useSelectActiveFilter()

  const dateFilterStart = dateFilterRange.start ?? DateFormatter.getToday()
  const dateFilterEnd = dateFilterRange.end ?? DateFormatter.endOfDay(dateFilterStart)

  const baseVariables: EventsListQueryVariables = {
    boundingBox: boundingBox!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    locationShapes: [locationShape?.id!],
    categorySlugs: [activeFilter.id],
    timeRanges: [{ endTime: dateFilterEnd, startTime: dateFilterStart }],
    page: 1,
    perPage: FEED_DEFAULT_PER_PAGE,
    width: PREVIEW_IMAGE_WIDTH,
    height: PREVIEW_IMAGE_HEIGHT
  }

  const { data, fetchMore, loading } = useEventsList({
    variables: baseVariables,
    skip: !locationShape || !boundingBox
  })

  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    if (data?.events.pageInfo.nextPage && !loadingRef.current) {
      fetchMore({ variables: { page: data.events.pageInfo.nextPage } })
    }
  }, [data?.events.pageInfo.nextPage, fetchMore])

  const eventListItems = data?.events?.items ?? []

  const markers = createMarkersList(eventListItems)

  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set())

  const onVisibleChange = useCallback((id: string, inView: boolean) => {
    setVisibleIds(prev => {
      const next = new Set(prev)
      if (inView) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const markersInView = useMemo(
    () => markers.filter(m => visibleIds.has(m.id)),
    [markers, visibleIds]
  )

  // TODO: get category title again
  const title =
    locationShape && activeFilter.label
      ? EVENTSFEED_CATEGORY_TITLE(locationShape.name, activeFilter.label)
      : undefined

  const description =
    locationShape && EVENTSFEED_DESCRIPTION(locationShape.name)

  const { filter } = useParams<{ filter: string }>()

  const setFilter = useSetActiveFilter()
  useEffect(() => {
    setFilter({
      id: filter,
      label: ''
    })
  }, [setFilter, filter])

  usePageTracking({ pageTitle: title, user })

  return (
    <>
      <MetaTags
        description={description}
        geo_placename={locationShape?.name}
        geo_position={
          locationShape &&
          `${locationShape.center.latitude};${locationShape.center.longitude}`
        }
        og_image={EVENTSFEED_OG_IMAGE}
        og_url={document.location.toString()}
        title={title}
      />
      <LayoutFeaturedContainer
        markers={markersInView}
        locationShape={locationShape}
        subHeader={<HeaderEventCategory />}
        activeNavigationItem={FEED_TYPE_EVENT}
        pageTitle={<PageTitleEventCategory />}
        withHeaderMargin
      >
        <EventCategoryFeed
          getNextPage={getNextPage}
          loading={loading}
          eventFeedData={data?.events?.items ?? []}
          isPostCreateOpen={isPostCreateOpen}
          onVisibleChange={onVisibleChange}
        />
      </LayoutFeaturedContainer>
    </>
  )
}

export default EventCategoryFeedPage
